import React, { useEffect, useRef, useState } from 'react';
import SpotMapControls from '../map-controls';
import SHGoogleMap from '../google-map';
import { foldRecentlyViewedIntoSpots, transformSpotToMarkerData, } from './spot-map-utils';
import { Box } from '@spothero/ui';
import { DEFAULT_ZOOM_LEVEL, HIGHEST_ALLOWED_ZOOM_LEVEL, LOWEST_ALLOWED_ZOOM_LEVEL, } from '../google-map/utils/constants';
import { useSelector } from 'react-redux';
import { useShowTotal } from 'store/selectors/hooks/useShowTotal';
import { MapLoader } from './map-loader/MapLoader';
import throttle from 'lodash/throttle';
import { MANUAL_ZOOM_IN_EVENT, MANUAL_ZOOM_OUT_EVENT, } from 'utils/window-events/constants';
/**
 * SpotMap is the middle "layer" of the map components (SearchMap, SpotMap, SHGoogleMap).
 *
 * This component is responsible for:
 * - Preparing spot marker data from a list of spots
 * - Coordination between SHGoogleMap and various controls (zoom in/out, return to destination)
 * - Display of NoResultsModal when user is on mobile map view.
 * - Handling zoom and pan events from the map
 *
 * @param {SpotMapProps} props - top level properties
 * @returns {React.Component} - Spot Map component
 */
const SpotMap = (props) => {
    const [zoom, setZoom] = useState(DEFAULT_ZOOM_LEVEL);
    const { searchRequest: { powerBooking: isPowerBooking }, } = useSelector((state) => state);
    const showTotal = useShowTotal();
    const [isDestinationOutOfBounds, setIsDestinationOutOfBounds] = useState(false);
    const returnToDestination = useRef(null);
    // Below enables us to know when the user has interacted with the map after load
    const hasInteracted = useRef(false);
    const eventManualZoomInteraction = () => {
        hasInteracted.current = true;
    };
    const { searchRequestId, destination, spots, onMarkerClick, isAdmin, recentlyViewedSpots, showAllSpots, onZoom, onPan, } = props;
    useEffect(() => {
        function clearHandlers() {
            /* eslint-disable @typescript-eslint/no-use-before-define */
            document.removeEventListener('click', handleInteraction);
            document.removeEventListener('keydown', handleInteraction);
            document.removeEventListener('touchstart', handleInteraction);
            document.removeEventListener('touchmove', handleInteraction);
            document.removeEventListener('mousewheel', handleInteraction);
            document.removeEventListener('wheel', handleWheel);
            window.removeEventListener(MANUAL_ZOOM_IN_EVENT, eventManualZoomInteraction);
            window.removeEventListener(MANUAL_ZOOM_OUT_EVENT, eventManualZoomInteraction);
            /* eslint-disable @typescript-eslint/no-use-before-define */
        }
        function handleInteraction() {
            hasInteracted.current = true;
            clearHandlers();
        }
        // This has to work for any time we do a wheel event to pick up touch pads - this seems to behave as expected
        function handleWheel() {
            handleInteraction();
        }
        document.addEventListener('click', handleInteraction);
        document.addEventListener('keydown', handleInteraction);
        document.addEventListener('touchstart', handleInteraction);
        document.addEventListener('touchmove', handleInteraction);
        document.addEventListener('mousewheel', handleInteraction);
        document.addEventListener('wheel', handleWheel);
        window.addEventListener(MANUAL_ZOOM_IN_EVENT, eventManualZoomInteraction);
        window.addEventListener(MANUAL_ZOOM_OUT_EVENT, eventManualZoomInteraction);
        return () => {
            hasInteracted.current = false;
            clearHandlers();
        };
    }, []);
    const onGoogleMapLoaded = (value) => {
        if (value) {
            returnToDestination.current = value;
        }
    };
    // fired when the user zooms using pinch/scroll as well as after a UI control is used to zoom
    const onMapZoomChanged = throttle(({ zoom: newZoom, prevZoom, mapCenter, mapBounds }) => {
        setZoom(newZoom);
        if (hasInteracted.current) {
            onZoom({
                zoom: newZoom,
                prevZoom,
                mapCenter,
                mapBounds,
            });
        }
    }, 75);
    const handleMapPan = ({ zoom: newZoom, mapCenter, mapBounds, isDestinationOutOfBounds: isOutOfBounds, }) => {
        if (onPan) {
            onPan({
                mapCenter,
                mapBounds,
            });
        }
        setZoom(newZoom);
        setIsDestinationOutOfBounds(isOutOfBounds);
    };
    const spotsWRecentlyViewed = foldRecentlyViewedIntoSpots({
        spots,
        recentlyViewedSpots: Array.isArray(recentlyViewedSpots)
            ? recentlyViewedSpots
            : [],
    });
    const transformedSpots = spotsWRecentlyViewed.map(spot => transformSpotToMarkerData(spot, isPowerBooking, showTotal));
    return (<Box className="SpotMap">
            <Box position="relative" sx={{
            '.do-not-enter': {
                paddingBottom: '0.25rem',
            },
        }} className="SpotMap-map">
                <MapLoader />
                <SHGoogleMap isAdmin={isAdmin} searchRequestId={searchRequestId} spots={transformedSpots} zoom={zoom} destination={destination} onPan={handleMapPan} onZoom={onMapZoomChanged} onMarkerClick={onMarkerClick} onGoogleMapLoaded={onGoogleMapLoaded} showAllSpots={showAllSpots}/>
            </Box>
            <SpotMapControls isDestinationOutOfBounds={isDestinationOutOfBounds} isZoomInDisabled={zoom >= HIGHEST_ALLOWED_ZOOM_LEVEL} isZoomOutDisabled={zoom <= LOWEST_ALLOWED_ZOOM_LEVEL} 
    // eslint-disable-next-line react/jsx-handler-names
    onReturnToDestination={returnToDestination.current}/>
        </Box>);
};
export default SpotMap;
